import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Trash2, 
  Copy, 
  Save, 
  Send, 
  Calculator,
  Target,
  Clock,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { EnhancedPEOQuote, QuoteLineItem, QuotePlanOption } from '@/types/quotes-enhanced.types';

interface EnterpriseQuoteBuilderProps {
  applicationId: string;
  providerId: string;
  onSave: (quote: Partial<EnhancedPEOQuote>) => void;
  onSubmit: (quoteId: string) => void;
  existingQuote?: EnhancedPEOQuote;
}

export const EnterpriseQuoteBuilder: React.FC<EnterpriseQuoteBuilderProps> = ({
  applicationId,
  providerId,
  onSave,
  onSubmit,
  existingQuote
}) => {
  const [quote, setQuote] = useState<Partial<EnhancedPEOQuote>>(existingQuote || {
    application_id: applicationId,
    provider_id: providerId,
    quote_summary: '',
    status: 'pending',
    line_items: [],
    plan_options: [],
    implementation_timeline: []
  });

  const [activeTab, setActiveTab] = useState('summary');
  const [isCalculating, setIsCalculating] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const addLineItem = () => {
    const newItem: Partial<QuoteLineItem> = {
      service_name: '',
      description: '',
      current_cost: 0,
      proposed_cost: 0,
      is_included: false,
      is_optional: false
    };

    setQuote(prev => ({
      ...prev,
      line_items: [...(prev.line_items || []), newItem as QuoteLineItem]
    }));
  };

  const updateLineItem = (index: number, field: keyof QuoteLineItem, value: any) => {
    setQuote(prev => ({
      ...prev,
      line_items: prev.line_items?.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ) || []
    }));
  };

  const removeLineItem = (index: number) => {
    setQuote(prev => ({
      ...prev,
      line_items: prev.line_items?.filter((_, i) => i !== index) || []
    }));
  };

  const addPlanOption = () => {
    const newPlan: Partial<QuotePlanOption> = {
      plan_name: '',
      plan_type: 'standard',
      monthly_cost: 0,
      annual_cost: 0,
      features: [],
      is_recommended: false
    };

    setQuote(prev => ({
      ...prev,
      plan_options: [...(prev.plan_options || []), newPlan as QuotePlanOption]
    }));
  };

  const updatePlanOption = (index: number, field: keyof QuotePlanOption, value: any) => {
    setQuote(prev => ({
      ...prev,
      plan_options: prev.plan_options?.map((plan, i) => 
        i === index ? { ...plan, [field]: value } : plan
      ) || []
    }));
  };

  const removePlanOption = (index: number) => {
    setQuote(prev => ({
      ...prev,
      plan_options: prev.plan_options?.filter((_, i) => i !== index) || []
    }));
  };

  const calculateTotals = () => {
    setIsCalculating(true);
    
    setTimeout(() => {
      const totalCurrent = quote.line_items?.reduce(
        (sum, item) => sum + (item.current_cost || 0), 0
      ) || 0;
      
      const totalProposed = quote.line_items?.reduce(
        (sum, item) => sum + (item.proposed_cost || 0), 0
      ) || 0;

      const monthlyTotal = totalProposed / 12;
      
      setQuote(prev => ({
        ...prev,
        monthly_cost: monthlyTotal,
        annual_cost: totalProposed
      }));

      setIsCalculating(false);
      toast.success('Quote totals calculated successfully');
    }, 1000);
  };

  const handleSave = () => {
    onSave(quote);
    toast.success('Quote saved successfully');
  };

  const handleSubmit = () => {
    if (quote.id) {
      onSubmit(quote.id);
      toast.success('Quote submitted to client');
    }
  };

  const duplicateQuote = () => {
    setQuote(prev => ({
      ...prev,
      id: undefined,
      quote_summary: `${prev.quote_summary} (Copy)`,
      status: 'pending'
    }));
    toast.success('Quote duplicated');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Quote Builder</h2>
          <p className="text-muted-foreground">Create comprehensive PEO quotes with detailed breakdowns</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={duplicateQuote}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicate
          </Button>
          <Button variant="outline" onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Draft
          </Button>
          {quote.id && (
            <Button onClick={handleSubmit}>
              <Send className="h-4 w-4 mr-2" />
              Submit Quote
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="lineItems">Line Items</TabsTrigger>
          <TabsTrigger value="plans">Plan Options</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quote Summary</CardTitle>
              <CardDescription>Basic quote information and overview</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="quote-summary">Quote Summary</Label>
                  <Textarea
                    id="quote-summary"
                    placeholder="Enter a comprehensive quote summary..."
                    value={quote.quote_summary || ''}
                    onChange={(e) => setQuote(prev => ({ ...prev, quote_summary: e.target.value }))}
                    rows={4}
                  />
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="monthly-cost">Monthly Cost</Label>
                    <Input
                      id="monthly-cost"
                      type="number"
                      placeholder="0"
                      value={quote.monthly_cost || ''}
                      onChange={(e) => setQuote(prev => ({ 
                        ...prev, 
                        monthly_cost: parseFloat(e.target.value) || 0 
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="annual-cost">Annual Cost</Label>
                    <Input
                      id="annual-cost"
                      type="number"
                      placeholder="0"
                      value={quote.annual_cost || ''}
                      onChange={(e) => setQuote(prev => ({ 
                        ...prev, 
                        annual_cost: parseFloat(e.target.value) || 0 
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="valid-until">Valid Until</Label>
                    <Input
                      id="valid-until"
                      type="date"
                      value={quote.valid_until || ''}
                      onChange={(e) => setQuote(prev => ({ ...prev, valid_until: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lineItems" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Line Items</CardTitle>
                  <CardDescription>Detailed cost breakdown by service category</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={calculateTotals}
                    disabled={isCalculating}
                  >
                    <Calculator className="h-4 w-4 mr-2" />
                    {isCalculating ? 'Calculating...' : 'Calculate Totals'}
                  </Button>
                  <Button onClick={addLineItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Line Item
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {quote.line_items?.map((item, index) => (
                  <Card key={index} className="border-l-4 border-l-primary">
                    <CardContent className="pt-4">
                      <div className="grid gap-4 md:grid-cols-6">
                        <div className="md:col-span-2">
                          <Label>Service Name</Label>
                          <Input
                            placeholder="Service name"
                            value={item.service_name}
                            onChange={(e) => updateLineItem(index, 'service_name', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Current Cost</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={item.current_cost || ''}
                            onChange={(e) => updateLineItem(index, 'current_cost', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div>
                          <Label>Proposed Cost</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={item.proposed_cost || ''}
                            onChange={(e) => updateLineItem(index, 'proposed_cost', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={item.is_included}
                              onCheckedChange={(checked) => updateLineItem(index, 'is_included', checked)}
                            />
                            <Label>Included</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={item.is_optional}
                              onCheckedChange={(checked) => updateLineItem(index, 'is_optional', checked)}
                            />
                            <Label>Optional</Label>
                          </div>
                        </div>
                        <div className="flex items-end">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3">
                        <Label>Description</Label>
                        <Textarea
                          placeholder="Service description..."
                          value={item.description || ''}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          rows={2}
                        />
                      </div>
                    </CardContent>
                  </Card>
                )) || []}

                {(!quote.line_items || quote.line_items.length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No line items added yet. Click "Add Line Item" to get started.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="plans" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Plan Options</CardTitle>
                  <CardDescription>Different coverage levels and pricing tiers</CardDescription>
                </div>
                <Button onClick={addPlanOption}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Plan
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {quote.plan_options?.map((plan, index) => (
                  <Card key={index} className={cn(
                    "relative",
                    plan.is_recommended && "ring-2 ring-primary"
                  )}>
                    {plan.is_recommended && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground">
                          <Target className="h-3 w-3 mr-1" />
                          Recommended
                        </Badge>
                      </div>
                    )}
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <Input
                            placeholder="Plan name"
                            value={plan.plan_name}
                            onChange={(e) => updatePlanOption(index, 'plan_name', e.target.value)}
                            className="font-medium"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removePlanOption(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <Label>Plan Type</Label>
                        <Select
                          value={plan.plan_type || 'standard'}
                          onValueChange={(value) => updatePlanOption(index, 'plan_type', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="standard">Standard</SelectItem>
                            <SelectItem value="premium">Premium</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>Monthly</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={plan.monthly_cost || ''}
                            onChange={(e) => updatePlanOption(index, 'monthly_cost', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div>
                          <Label>Annual</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={plan.annual_cost || ''}
                            onChange={(e) => updatePlanOption(index, 'annual_cost', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={plan.is_recommended}
                          onCheckedChange={(checked) => updatePlanOption(index, 'is_recommended', checked)}
                        />
                        <Label>Recommended Plan</Label>
                      </div>
                    </CardContent>
                  </Card>
                )) || []}

                {(!quote.plan_options || quote.plan_options.length === 0) && (
                  <div className="col-span-full text-center py-8 text-muted-foreground">
                    <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No plan options added yet. Click "Add Plan" to create different coverage levels.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Implementation Timeline
              </CardTitle>
              <CardDescription>Project milestones and estimated durations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Timeline builder coming soon...</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quote Preview</CardTitle>
              <CardDescription>How your quote will appear to the client</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Quote Summary */}
                <div>
                  <h3 className="text-lg font-semibold mb-2">Quote Summary</h3>
                  <p className="text-muted-foreground">{quote.quote_summary || 'No summary provided'}</p>
                </div>

                {/* Cost Overview */}
                <div className="grid gap-4 md:grid-cols-3">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {quote.monthly_cost ? formatCurrency(quote.monthly_cost) : 'TBD'}
                      </div>
                      <p className="text-sm text-muted-foreground">Monthly Cost</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold">
                        {quote.annual_cost ? formatCurrency(quote.annual_cost) : 'TBD'}
                      </div>
                      <p className="text-sm text-muted-foreground">Annual Cost</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {quote.line_items?.filter(item => item.is_included).length || 0}
                      </div>
                      <p className="text-sm text-muted-foreground">Included Services</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Plan Options Preview */}
                {quote.plan_options && quote.plan_options.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Available Plans</h3>
                    <div className="grid gap-4 md:grid-cols-3">
                      {quote.plan_options.map((plan, index) => (
                        <Card key={index} className={cn(
                          plan.is_recommended && "ring-2 ring-primary"
                        )}>
                          <CardHeader>
                            <CardTitle className="text-lg">{plan.plan_name}</CardTitle>
                            {plan.is_recommended && (
                              <Badge className="w-fit">Recommended</Badge>
                            )}
                          </CardHeader>
                          <CardContent>
                            <div className="text-center">
                              <div className="text-2xl font-bold">
                                {plan.monthly_cost ? formatCurrency(plan.monthly_cost) : 'TBD'}
                              </div>
                              <p className="text-sm text-muted-foreground">per month</p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};